import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle, Check, X as XIcon, FileText } from 'lucide-react';
import {
  fetchAgentLog, approveAgentAction, rejectAgentAction,
  type AgentActionLogRow,
} from '@/lib/api';

/**
 * Phase 5a Item 10 — admin Agent Log page.
 *
 * Surfaces every AgentActionLog row. Auto-actions are visible for
 * oversight; pending_approval rows are actionable (approve/reject
 * inline). super_admin sees all tenants; tenant_admin sees their
 * own — server-enforced scoping on /admin/agent-log.
 */

interface AgentLogPageProps {
  onNavigate: (path: string) => void;
}

function fmt(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function OutcomeBadge({ outcome }: { outcome: AgentActionLogRow['outcome'] }) {
  const colorMap: Record<AgentActionLogRow['outcome'], string> = {
    success: 'var(--evi-accent, #2ECC71)',
    failed: 'var(--evi-error, #ef4444)',
    pending_approval: 'var(--evi-warning, #f59e0b)',
    rejected: 'var(--color-tx-m)',
    timed_out: 'var(--color-tx-m)',
  };
  return (
    <span style={{
      padding: '2px 8px', fontSize: 11, borderRadius: 4,
      background: `color-mix(in srgb, ${colorMap[outcome]} 16%, transparent)`,
      color: colorMap[outcome],
    }}>
      {outcome}
    </span>
  );
}

export function AgentLogPage({ onNavigate }: AgentLogPageProps) {
  void onNavigate;
  const qc = useQueryClient();
  const [outcomeFilter, setOutcomeFilter] = useState<AgentActionLogRow['outcome'] | ''>('');
  const [autoFilter, setAutoFilter] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState<AgentActionLogRow | null>(null);

  // Deep-link support — pre-open a row if action_log_id is in the URL.
  // (App.tsx's path-parser passes a clean `/agent-log` here, so we
  // pull from window.location.search ourselves.)
  const initialActionLogId = (() => {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get('action_log_id');
  })();

  const listQ = useQuery({
    queryKey: ['agent-log', outcomeFilter, autoFilter, page],
    queryFn: () => fetchAgentLog({
      outcome: outcomeFilter || undefined,
      was_auto: autoFilter === 'true' ? true : autoFilter === 'false' ? false : undefined,
      page,
      page_size: 25,
    }),
  });

  const approveM = useMutation({
    mutationFn: (id: string) => approveAgentAction(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent-log'] });
      setOpenRow(null);
    },
  });
  const rejectM = useMutation({
    mutationFn: (id: string) => rejectAgentAction(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent-log'] });
      setOpenRow(null);
    },
  });

  // Pre-open the deep-linked row once data lands.
  if (initialActionLogId && !openRow && listQ.data) {
    const match = listQ.data.data.find((r) => r._id === initialActionLogId);
    if (match) setOpenRow(match);
  }

  return (
    <div style={{ padding: '24px 28px' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>
          <FileText size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Agent Log
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-tx-m)' }}>
          Every autonomous-agent action plus admin-tool dispatches. Pending-approval rows are actionable.
        </p>
      </header>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 13 }}>
        <label>
          Outcome:{' '}
          <select
            value={outcomeFilter}
            onChange={(e) => { setOutcomeFilter(e.target.value as AgentActionLogRow['outcome'] | ''); setPage(1); }}
          >
            <option value="">All</option>
            <option value="pending_approval">pending_approval</option>
            <option value="success">success</option>
            <option value="failed">failed</option>
            <option value="rejected">rejected</option>
            <option value="timed_out">timed_out</option>
          </select>
        </label>
        <label>
          Trigger:{' '}
          <select value={autoFilter} onChange={(e) => { setAutoFilter(e.target.value as '' | 'true' | 'false'); setPage(1); }}>
            <option value="">All</option>
            <option value="true">auto</option>
            <option value="false">human-triggered</option>
          </select>
        </label>
      </div>

      {listQ.isLoading && <p><Loader2 size={14} className="spin" /> Loading…</p>}
      {listQ.isError && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          <AlertTriangle size={14} /> Could not load agent log.
        </p>
      )}
      {listQ.data && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))', textAlign: 'left' }}>
                <th style={{ padding: 6 }}>Time</th>
                <th style={{ padding: 6 }}>Agent</th>
                <th style={{ padding: 6 }}>Action</th>
                <th style={{ padding: 6 }}>Outcome</th>
                <th style={{ padding: 6 }}>Auto?</th>
                <th style={{ padding: 6 }}>Approved by</th>
                <th style={{ padding: 6, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.data.map((r) => (
                <tr key={r._id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                  <td style={{ padding: 6, color: 'var(--color-tx-m)', whiteSpace: 'nowrap' }}>{fmt(r.timestamp)}</td>
                  <td style={{ padding: 6 }}><code>{r.agent_identity}</code></td>
                  <td style={{ padding: 6 }}><code>{r.action_type}</code></td>
                  <td style={{ padding: 6 }}><OutcomeBadge outcome={r.outcome} /></td>
                  <td style={{ padding: 6 }}>{r.was_auto ? 'yes' : 'no'}</td>
                  <td style={{ padding: 6, color: 'var(--color-tx-m)' }}>
                    {r.approved_by_user_id ? String(r.approved_by_user_id).slice(-6) : '—'}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => setOpenRow(r)}
                      style={{ background: 'transparent', border: 'none', color: 'var(--evi-accent, #2ECC71)', cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {listQ.data.data.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-tx-m)' }}>
                  No agent actions match the current filters.
                </td></tr>
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
            <button type="button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span style={{ padding: '6px 12px', color: 'var(--color-tx-m)', fontSize: 12 }}>
              Page {page} of {Math.max(1, Math.ceil(listQ.data.meta.total / listQ.data.meta.page_size))}
            </span>
            <button type="button" disabled={page * listQ.data.meta.page_size >= listQ.data.meta.total} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </>
      )}

      {openRow && (
        <div
          role="dialog"
          onClick={() => setOpenRow(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(640px, 90vw)', maxHeight: '80vh', overflowY: 'auto',
              padding: 24, background: 'var(--color-sf, #111)',
              border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 17 }}>Agent action detail</h2>
              <button type="button" onClick={() => setOpenRow(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <XIcon size={16} />
              </button>
            </div>

            <dl style={{ display: 'grid', gridTemplateColumns: 'var(--admin-definition-columns, 160px 1fr)', gap: 6, fontSize: 12 }}>
              <dt style={{ color: 'var(--color-tx-m)' }}>Timestamp</dt><dd>{fmt(openRow.timestamp)}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Agent</dt><dd><code>{openRow.agent_identity}</code></dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Action</dt><dd><code>{openRow.action_type}</code></dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Outcome</dt><dd><OutcomeBadge outcome={openRow.outcome} /></dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Trigger</dt><dd>{openRow.was_auto ? 'auto' : 'human'}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Alert</dt>
              <dd>{openRow.alert_id ? <code>{String(openRow.alert_id).slice(-8)}</code> : '—'}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Charger</dt>
              <dd>{openRow.charger_id ? <code>{String(openRow.charger_id).slice(-8)}</code> : '—'}</dd>
              {openRow.error_message && (
                <>
                  <dt style={{ color: 'var(--color-tx-m)' }}>Error</dt>
                  <dd style={{ color: 'var(--evi-error, #ef4444)' }}>{openRow.error_message}</dd>
                </>
              )}
              {openRow.approval_note && (
                <>
                  <dt style={{ color: 'var(--color-tx-m)' }}>Approval note</dt>
                  <dd>{openRow.approval_note}</dd>
                </>
              )}
            </dl>

            <h3 style={{ marginTop: 16, marginBottom: 6, fontSize: 12 }}>Parameters</h3>
            <pre style={{
              margin: 0, fontSize: 11, padding: 10,
              background: 'var(--color-sf2, #1a1a1a)', borderRadius: 6, maxHeight: 240, overflow: 'auto',
            }}>
              {JSON.stringify(openRow.action_parameters, null, 2)}
            </pre>

            {openRow.outcome === 'pending_approval' && (
              <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => approveM.mutate(openRow._id)}
                  disabled={approveM.isPending}
                  style={{
                    padding: '8px 14px', fontSize: 13,
                    background: 'var(--evi-accent, #2ECC71)', color: '#000',
                    border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Check size={11} style={{ verticalAlign: 'middle' }} /> Approve & execute
                </button>
                <button
                  type="button"
                  onClick={() => rejectM.mutate(openRow._id)}
                  disabled={rejectM.isPending}
                  style={{
                    padding: '8px 14px', fontSize: 13,
                    background: 'transparent', color: 'var(--evi-error, #ef4444)',
                    border: '1px solid var(--evi-error, #ef4444)', borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  <XIcon size={11} style={{ verticalAlign: 'middle' }} /> Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
